import type { DashboardNotice } from "../lib/notices/types";
import { validateOfficialUrl } from "../lib/validation/url";

export interface BookmarkedReportOptions {
  generatedAt?: Date;
}

function markdownInline(value: string): string {
  return value
    .replace(/\r?\n/gu, " / ")
    .replace(/([\\`*_{}\[\]<>#+.!|()-])/gu, "\\$1")
    .trim();
}

function reportDate(value: string | null): string {
  if (!value) return "미확인";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "미확인" : parsed.toISOString().slice(0, 10);
}

function deadlineLabel(notice: DashboardNotice): string {
  if (notice.dday === null) return "D-day 미확인";
  if (notice.dday < 0) return `마감 ${Math.abs(notice.dday)}일 경과`;
  if (notice.dday === 0) return "D-Day";
  return `D-${notice.dday}`;
}

function deadlineSortValue(notice: DashboardNotice): number {
  const value = Date.parse(notice.applicationEndAt ?? "");
  return Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER;
}

export function buildBookmarkedNoticesMarkdown(
  notices: readonly DashboardNotice[],
  options: BookmarkedReportOptions = {},
): string {
  const generatedAt = options.generatedAt ?? new Date();
  const bookmarked = notices
    .filter((notice) => notice.user?.bookmarked)
    .sort((left, right) => {
      const deadlineDifference = deadlineSortValue(left) - deadlineSortValue(right);
      return deadlineDifference || left.title.localeCompare(right.title, "ko-KR");
    });
  const lines = [
    "# 관심 공고 보고서",
    "",
    `- 생성시각: ${generatedAt.toISOString()}`,
    `- 관심 공고: ${bookmarked.length}건`,
    "",
  ];

  if (bookmarked.length === 0) {
    lines.push("관심 공고가 없습니다.", "");
  }

  for (const notice of bookmarked) {
    const officialUrl = validateOfficialUrl(notice.originalUrl);
    const user = notice.user;
    lines.push(
      `## ${markdownInline(notice.title)}`,
      "",
      `- 기관: ${markdownInline(notice.agency)}`,
      `- 출처: ${notice.sourceNames.map(markdownInline).join(", ") || "미확인"}`,
      `- 마감일: ${reportDate(notice.applicationEndAt)} · ${deadlineLabel(notice)}`,
      `- 신청자격: ${markdownInline(notice.evaluation?.eligibilityStatus ?? "미확인")}`,
      `- 적합도: ${notice.evaluation ? `${notice.evaluation.fitScore}점 · ${markdownInline(notice.evaluation.fitGrade)}` : "미평가"}`,
      `- 검토상태: ${markdownInline(user?.reviewStatus ?? "미검토")}`,
    );
    if (user?.assignee.trim()) lines.push(`- 담당자: ${markdownInline(user.assignee)}`);
    if (user?.note.trim()) lines.push(`- 메모: ${markdownInline(user.note)}`);
    lines.push(
      `- 공식 원문: ${officialUrl.valid && officialUrl.normalizedUrl ? officialUrl.normalizedUrl : "확인 필요"}`,
      "",
    );
  }

  return `${lines.join("\n")}\n`;
}
